import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import api from '../api';
import { Button } from '../components/ui/button';
import {
  ArrowRight, CheckCircle2, Bike, Sparkles, Shield, Target,
  Brain, Zap, Clock, PlayCircle, Award, GraduationCap, Route, Map,
  AlertTriangle, CloudSun, Flag, Trophy, Timer, Cloud, CloudRain, CloudSnow, Sun, MapPin, Film,
  BookOpen, Gift, Lock, Download,
} from 'lucide-react';
import { HERO_IMAGE, FORMATION_IMAGES, SECURITE_ROUTIERE, getNextMotoGP, daysUntil } from '../data/homeLive';
import { Reveal } from '../components/Reveal';
import LiveActivityBar from '../components/LiveActivityBar';
import RelatedLinks from '../components/RelatedLinks';
import HomeLivretSection from '../components/HomeLivretSection';
import HomeMagazineEtmSection from '../components/HomeMagazineEtmSection';
import HomeShortsCarousel from '../components/HomeShortsCarousel';
import ShortsPromoBanner from '../components/ShortsPromoBanner';
import HomeEnquetesSection from '../components/HomeEnquetesSection';
import FeaturedDemosSection from '../components/FeaturedDemosSection';
import useAudience, { AUDIENCES } from '../hooks/useAudience';
import NewConceptBanner from '../components/NewConceptBanner';
import CampaignBanner from '../components/CampaignBanner';
import HomeServicesGrid from '../components/HomeServicesGrid';
import GuidesPremium from '../components/home/GuidesPremium';
import PlateauInteractive from '../components/home/PlateauInteractive';
import FinalCTA from '../components/home/FinalCTA';
import CrossSiteCallout from '../components/home/CrossSiteCallout';
import { Counter } from '../components/Counter';
import { MarqueeTicker } from '../components/MarqueeTicker';
import { NewsletterForm } from '../components/NewsletterForm';
import SecuriteRoutiereStats from '../components/SecuriteRoutiereStats';
import { Lightbulb } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { readCached, writeCached, clearCached } from '../lib/cachedState';
import { combineJsonLd, buildBreadcrumb } from '../seo/breadcrumbs';
import {
  buildWebSiteSchema,
  buildOrganizationSchema,
  buildItemList,
} from '../seo/schemas';
import { HomeProgressionSection } from '../components/home/HomeProgressionSection';
import BreakingSecurityAlert from '../components/home/BreakingSecurityAlert';

const weatherIcon = (cond = '') => {
  const c = cond.toLowerCase();
  if (c.includes('rain') || c.includes('pluie') || c.includes('drizzle')) return CloudRain;
  if (c.includes('snow') || c.includes('neige')) return CloudSnow;
  if (c.includes('cloud') || c.includes('nuag') || c.includes('overcast')) return Cloud;
  if (c.includes('clear') || c.includes('sun') || c.includes('ensoleill')) return Sun;
  return CloudSun;
};

const weatherAdvice = (cond = '', temp = null) => {
  const c = cond.toLowerCase();
  if (c.includes('rain') || c.includes('pluie') || c.includes('drizzle')) return { label: 'Prudence', color: '#b42318', text: 'Pluie : distances doublées, freinage progressif.' };
  if (c.includes('snow') || c.includes('neige')) return { label: 'Danger', color: '#b42318', text: 'Neige ou verglas : évite de rouler.' };
  if (c.includes('fog') || c.includes('brume') || c.includes('mist')) return { label: 'Attention', color: '#f59e0b', text: 'Brouillard : feux, vitesse réduite.' };
  if (temp !== null && temp < 5) return { label: 'Attention', color: '#f59e0b', text: 'Températures basses : risque de verglas.' };
  if (temp !== null && temp > 30) return { label: 'Vigilance', color: '#f59e0b', text: 'Forte chaleur : hydrate-toi, évite les heures de pointe.' };
  return { label: 'Bonnes conditions', color: '#18794e', text: 'Conditions favorables pour rouler.' };
};

export default function Home() {
  const { user } = useApp();
  const navigate = useNavigate();
  const { audience, setAudience } = useAudience();
  // 🛡️ Cache localStorage sur toutes les données dynamiques pour éliminer
  // le « Flash of Stale Content » au retour sur la home (entre le HTML
  // pre-rendered Puppeteer et l'hydratation React).
  // TTL différencié selon la fraîcheur attendue de la donnée :
  //   • formations    → 24h (données stables, mises à jour rares)
  //   • stats publics → 24h (chiffres totaux, pas critiques)
  //   • hero          → 24h (édition hebdo, change peu)
  //   • gamification  → 5min (données utilisateur, doivent rester fraîches)
  const TTL_LONG = 24 * 60 * 60 * 1000;     // 24h
  const TTL_SHORT = 5 * 60 * 1000;          // 5 min

  const [formations, setFormations] = useState(() =>
    readCached('formations', [], TTL_LONG),
  );
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [stats, setStats] = useState(() =>
    readCached('publicStats', { questions: 719, themes: 9, modules: 38 }, TTL_LONG),
  );
  // Édition hebdomadaire courante du hero (concept "magazine"). Fallback dynamique
  // côté backend si aucune édition n'est publiée → on récupère toujours au moins
  // un volume_label = "Volume X — Mois ANNÉE" calculé depuis la semaine ISO courante.
  const [heroEdition, setHeroEdition] = useState(() =>
    readCached('hero', null, TTL_LONG),
  );
  // Données gamification — cache 5min car données user, mais on évite quand même
  // un flash sur l'anneau XP au retour sur la home.
  const [gamification, setGamification] = useState(() =>
    readCached('gamification', null, TTL_SHORT),
  );
  const { progress } = useApp();

  useEffect(() => {
    api.getFormations()
      .then((data) => {
        setFormations(data || []);
        if (data && data.length > 0) writeCached('formations', data);
      })
      .catch(() => setFormations([]));
    api.getPublicStats()
      .then((data) => {
        if (data) {
          setStats(data);
          writeCached('publicStats', data);
        }
      })
      .catch(() => {});
    api.getCurrentHeroEdition()
      .then((data) => {
        setHeroEdition(data);
        if (data) writeCached('hero', data);
      })
      .catch(() => setHeroEdition(null));
  }, []);

  // Charge la gamification uniquement quand un user est connecté.
  // Au logout : on purge le cache pour ne pas exposer les données.
  useEffect(() => {
    if (!user) {
      setGamification(null);
      clearCached('gamification');
      return;
    }
    api.getGamification()
      .then((data) => {
        setGamification(data);
        if (data) writeCached('gamification', data);
      })
      .catch(() => setGamification(null));
  }, [user]);

  // SEO : page d'accueil — magazine pédagogique moto (positionnement éditeur, PAS un préparateur ETG)
  useSeo({
    title: `CODE 2 ROUES — Magazine pédagogique moto par des moniteurs ECSR`,
    description: `Le magazine pédagogique 100 % moto : dossiers thématiques, enquêtes sécurité routière, livrets plateau & circulation, tutos vidéo. Rédigé par des moniteurs ECSR. Sans pub, sans tracking.`,
    keywords: 'magazine moto, permis A1, permis A2, permis 125, livret plateau moto, livret circulation moto, tutos moto, ONISR, sécurité routière deux-roues',
    path: '/',
    preloadImage: (heroEdition && heroEdition.image_url) || HERO_IMAGE,
    jsonLd: combineJsonLd(
      buildWebSiteSchema(),
      buildOrganizationSchema(),
      buildBreadcrumb([]),
      buildItemList({
        name: '9 dossiers magazine · Programme pédagogique moto',
        items: [
          { name: 'Dossier · La route', path: '/revision?theme=R', description: 'Article pédagogique moto' },
          { name: 'Dossier · La circulation routière', path: '/revision?theme=C', description: 'Article pédagogique moto' },
          { name: 'Dossier · Le conducteur', path: '/revision?theme=L', description: 'Article pédagogique moto' },
          { name: 'Dossier · Les autres usagers', path: '/revision?theme=U', description: 'Article pédagogique moto' },
          { name: 'Dossier · Les notions diverses', path: '/revision?theme=D', description: 'Article pédagogique moto' },
          { name: 'Dossier · La mécanique et les équipements', path: '/revision?theme=M', description: 'Article pédagogique moto' },
          { name: 'Dossier · Les équipements de protection', path: '/revision?theme=P', description: 'Article pédagogique moto' },
          { name: "Dossier · L'environnement", path: '/revision?theme=E', description: 'Article pédagogique moto' },
          { name: 'Dossier · Les premiers secours', path: '/revision?theme=S', description: 'Article pédagogique moto' },
        ],
      }),
    ),
  });

  // Récupère météo via wttr.in (pas de clé API requise)
  useEffect(() => {
    let cancelled = false;
    const fetchWeather = async (query) => {
      try {
        const res = await fetch(`https://wttr.in/${encodeURIComponent(query)}?format=j1`);
        if (!res.ok) throw new Error('weather');
        const j = await res.json();
        if (cancelled) return;
        const current = j.current_condition?.[0];
        const area = j.nearest_area?.[0];
        setWeather({
          city: area?.areaName?.[0]?.value || query || 'Paris',
          region: area?.region?.[0]?.value || '',
          country: area?.country?.[0]?.value || '',
          temp_c: parseInt(current?.temp_C, 10),
          feels_c: parseInt(current?.FeelsLikeC, 10),
          condition: current?.lang_fr?.[0]?.value || current?.weatherDesc?.[0]?.value || '',
          wind_kmh: parseInt(current?.windspeedKmph, 10),
          humidity: parseInt(current?.humidity, 10)
        });
      } catch (e) {
        if (!cancelled) setWeather({ city: 'Paris', temp_c: null, condition: 'unavailable' });
      } finally {
        if (!cancelled) setWeatherLoading(false);
      }
    };
    // Essaie géoloc HTML5, fallback Paris
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchWeather(`${pos.coords.latitude},${pos.coords.longitude}`),
        () => fetchWeather('Paris'),
        { timeout: 3000, maximumAge: 600000 }
      );
    } else {
      fetchWeather('Paris');
    }
    return () => { cancelled = true; };
  }, []);

  const nextGP = getNextMotoGP();
  const gpDays = daysUntil(nextGP.date);
  const WeatherIcon = weather ? weatherIcon(weather.condition) : CloudSun;
  const advice = weather ? weatherAdvice(weather.condition, weather.temp_c) : null;

  return (
    <div className="space-y-10 sm:space-y-16 lg:space-y-20" data-testid="home-page">
      {/* ====== BANDEAU ALERTE SÉCURITÉ ROUTIÈRE (auto-expirant) ======
          Affiché si l'article du baromètre mensuel est en cours d'actualité.
          Disparaît automatiquement à la date d'expiration définie. */}
      <BreakingSecurityAlert />

      {/* CAMPAIGN BANNER — Nouvelle campagne nationale Sécurité routière 2026.
          Placé juste avant le hero pour maximum d'exposition. Refermable
          (persistance localStorage par slug de campagne). */}
      <CampaignBanner />

      {/* HERO ÉDITORIAL — empilement vertical (image en haut, texte en bas)
          Identique sur mobile et desktop, seules les proportions s'adaptent.
          • Image moto immersive en bandeau cinéma (aucun texte dessus)
          • Bloc texte en dessous : kicker rouge + H1 XXL + 2 CTA max */}
      <section
        className="relative overflow-hidden rounded-sm editorial-border-double bg-card hero-stack"
        data-testid="home-hero"
      >
        {/* ====== Image moto en bandeau cinéma + effet "en mouvement" ======
            Mobile : ratio ~5:3 paysage. Desktop : ratio ~21:9 cinémascope.
            Effet "véhicule en mouvement" 100% CSS (juin 2026) :
            • Ken Burns loop continu (pan + zoom subtil, 22s alternate)
            • Speed lines animées (bandes diagonales blanches traversant)
            • Respect prefers-reduced-motion : animation coupée si demandé */}
        <div className="relative w-full overflow-hidden aspect-[5/3] sm:aspect-[16/8] lg:aspect-[21/9] max-h-[640px]">
          {heroEdition?.video_url ? (
            // Édition vidéo : <video> en autoplay muet avec poster image fallback.
            // Respect prefers-reduced-motion : si l'utilisateur préfère réduire les
            // animations, on garde juste l'image poster (déclenché via CSS).
            <video
              key={heroEdition.video_url}
              src={heroEdition.video_url}
              poster={heroEdition.image_url || HERO_IMAGE}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover hero-image-cinematic motion-reduce:hidden"
              data-testid="hero-video"
              style={{ filter: 'saturate(0.92) contrast(1.04)' }}
            />
          ) : null}
          <img
            src={(heroEdition && heroEdition.image_url) || HERO_IMAGE}
            alt={(heroEdition && heroEdition.title) || 'Motard sur route au coucher du soleil'}
            className={`absolute inset-0 w-full h-full object-cover hero-image-cinematic ${heroEdition?.video_url ? 'motion-reduce:block hidden motion-reduce:visible' : ''}`}
            loading="eager"
            data-testid="hero-image"
            key={heroEdition?.image_url || 'static-image'}
            style={{ filter: 'saturate(0.92) contrast(1.04)' }}
            onError={(e) => {
              // Fallback automatique : si l'image de l'édition admin est cassée
              // (404, expirée, URL invalide), on bascule sur HERO_IMAGE pour
              // éviter un rectangle noir vide sur la home. Log silencieux.
              const fallback = HERO_IMAGE;
              if (e.currentTarget && e.currentTarget.src !== fallback) {
                e.currentTarget.src = fallback;
              }
            }}
          />

          {/* Speed lines — bandes diagonales qui traversent, illusion de vitesse */}
          <div className="hero-speed-lines" aria-hidden="true" data-testid="hero-speed-lines" />

          {/* Vignette subtile pour profondeur */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center, transparent 55%, rgba(15,12,8,0.35) 100%)',
            }}
          />
          {/* Dégradé bas pour fondre avec le bloc texte */}
          <div
            className="absolute inset-x-0 bottom-0 h-24 sm:h-32 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, transparent 0%, hsl(var(--card)) 100%)',
            }}
          />
        </div>

        {/* ====== Bloc texte sous l'image ====== */}
        <div
          className="relative px-6 pt-2 pb-10 sm:px-10 sm:pt-4 sm:pb-14 lg:px-16 lg:pt-6 lg:pb-20 max-w-5xl mx-auto hero-text-stack hero-content-fade"
          key={heroEdition?.id || 'static-hero'}
        >
          {/* Filet rouge éditorial à gauche (desktop) */}
          <div className="absolute left-0 top-2 bottom-10 w-[3px] bg-[hsl(var(--primary))] hidden lg:block" />

          {/* Micro kicker rouge */}
          <div className="flex items-center gap-2 mb-5 sm:mb-7 hero-fade-in" style={{ animationDelay: '120ms' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] flex-shrink-0" />
            <span
              className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary-on-dark))]"
              data-testid="hero-volume-label"
            >
              {(() => {
                const lbl = heroEdition?.volume_label || 'Volume 2026';
                return `À la une · ${lbl.replace(/^Volume\s+/i, 'Vol. ').replace(/\s+—\s+/g, ' · ')}`;
              })()}
            </span>
          </div>

          {/* TITRE XXL — magazine, line-height tassée pour impact maximum */}
          {heroEdition && heroEdition.title ? (
            <h1
              className="font-extrabold tracking-tight text-foreground max-w-4xl hero-title-xxl hero-fade-in"
              data-testid="hero-edition-title"
              style={{
                fontFamily: "'Playfair Display', 'Crimson Text', Georgia, serif",
                fontSize: 'clamp(2.4rem, 7vw, 5.5rem)',
                lineHeight: 0.95,
                animationDelay: '220ms',
              }}
            >
              {heroEdition.title}
            </h1>
          ) : (
            <h1
              className="font-extrabold tracking-tight text-foreground max-w-4xl hero-title-xxl hero-fade-in"
              style={{
                fontFamily: "'Playfair Display', 'Crimson Text', Georgia, serif",
                fontSize: 'clamp(2.4rem, 7vw, 5.5rem)',
                lineHeight: 0.95,
                animationDelay: '220ms',
              }}
            >
              La route<br />
              <span className="italic font-medium" style={{ color: '#f6efe0' }}>se partage.</span><br />
              <span className="text-[hsl(var(--primary))]">La passion</span>
              <span className="italic font-medium" style={{ color: '#f6efe0' }}> se transmet.</span>
            </h1>
          )}

          {/* 2 CTA MAX */}
          <div className="flex flex-wrap gap-3 mt-7 sm:mt-9 hide-in-oral hero-fade-in" style={{ animationDelay: '340ms' }}>
            {heroEdition?.link_url ? (
              <button
                onClick={() => navigate(heroEdition.link_url)}
                className="editorial-btn-primary hero-cta-glow"
                data-testid="hero-cta-magazine"
                style={{
                  background: 'hsl(var(--primary))',
                  borderColor: 'hsl(var(--primary))',
                  color: '#fff',
                }}
              >
                <BookOpen className="w-4 h-4" />
                {heroEdition.link_label || "Lire l'article"}
              </button>
            ) : (
              <button
                onClick={() => navigate('/parcours')}
                className="editorial-btn-primary hero-cta-glow"
                data-testid="hero-cta-main"
                style={{
                  background: 'hsl(var(--primary))',
                  borderColor: 'hsl(var(--primary))',
                  color: '#fff',
                }}
              >
                <Map className="w-4 h-4" /> Démarrer mon parcours
              </button>
            )}

            <button
              onClick={() => navigate('/enquetes')}
              className="editorial-btn-secondary text-foreground border-foreground/30 hover:bg-foreground hover:text-background transition-colors"
              data-testid="hero-cta-enquetes"
              style={{ borderColor: 'hsl(var(--foreground) / 0.30)' }}
            >
              <PlayCircle className="w-4 h-4" /> Découvrir les 31 enquêtes
            </button>
          </div>

          {/* Mini-footer ultra discret — repoussé plus loin et plus transparent */}
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-10 sm:mt-14 text-[10px] sm:text-[11px] text-foreground/45 hero-fade-in"
            style={{ animationDelay: '460ms' }}
          >
            <div className="inline-flex items-center gap-1.5">
              <div className="flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5 text-[hsl(var(--primary))]" />
              </div>
              <span className="font-bold text-foreground/55">Conçu par des moniteurs ECSR</span>
              <span className="hidden sm:inline">— aligné sur le programme officiel 2026</span>
            </div>
            <span className="hidden sm:inline text-foreground/20">·</span>
            <span className="inline-flex items-center gap-1">
              <Gift className="w-2.5 h-2.5 text-[hsl(var(--primary))]/70" />
              <span>100&nbsp;% gratuit · sans pub · sans tracking</span>
            </span>
          </div>
        </div>
      </section>

      {/* TICKER d'arguments — défilement infini */}
      <MarqueeTicker />

      {/* GRILLE SERVICES 3 colonnes — Permis AM · Formation 125 · Annuaire
          Mise en avant des 3 nouveautés 2026, exploite la largeur desktop. */}
      <HomeServicesGrid />

      {/* NEW CONCEPT BANNER — annonce le pivot magazine + QCM contextuel
          Refermable, persistance localStorage. Placée APRÈS le hero (juin 2026)
          pour ne plus surcharger le sommet de page avec 3 bandeaux dismissables. */}
      <NewConceptBanner />

      {/* LIVE ACTIVITY BAR — donne vie à la plateforme.
          MVP : messages générés localement (seed du jour, stable au refresh).
          V2 : brancher sur `/api/public/activity-pulse`. */}
      <div className="-mt-2 sm:-mt-4 px-1 sm:px-0" data-reveal="">
        <LiveActivityBar />
      </div>

      {/* ====================================================================
          PARCOURS PREMIUM — Permis AM & Formation 125 mis en évidence
          ==================================================================== */}
      <section className="mt-6 sm:mt-8" data-testid="home-parcours-premium" data-reveal="">
        {/* ==== BANDEAUX BRIEFINGS (accès rapide prof / élèves) ==== */}
        <div className="mb-4 grid sm:grid-cols-2 gap-2 sm:gap-3">
          <Link
            to="/briefing-circulation"
            reloadDocument
            className="hidden sm:flex items-center gap-3 rounded-2xl border-2 border-red-500/50 bg-gradient-to-r from-red-600/25 via-red-500/12 to-transparent p-3 sm:p-4 hover:border-red-500 hover:shadow-[0_0_35px_-10px_rgba(207,32,49,0.6)] active:scale-[0.99] transition-all"
            data-testid="home-briefing-shortcut"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-red-500/25 border-2 border-red-500/60 flex items-center justify-center text-xl">
              🏍️
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-300 mb-0.5">
                Briefing AM · Cyclomoteur
              </div>
              <div className="text-sm font-black leading-tight text-foreground truncate" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Mon Briefing Circulation
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                Sur le parking, avant de partir · QR + QCM
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-red-300 flex-shrink-0" />
          </Link>

          <Link
            to="/briefing-a2"
            reloadDocument
            className="flex items-center gap-3 rounded-2xl border-2 border-orange-500/50 bg-gradient-to-r from-orange-600/25 via-orange-500/12 to-transparent p-3 sm:p-4 hover:border-orange-500 hover:shadow-[0_0_35px_-10px_rgba(245,158,11,0.6)] active:scale-[0.99] transition-all"
            data-testid="home-briefing-a2-shortcut"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-orange-500/25 border-2 border-orange-500/60 flex items-center justify-center text-xl">
              🏁
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-300 mb-0.5">
                Briefing A2
              </div>
              <div className="text-sm font-black leading-tight text-foreground truncate" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Mon Briefing Moto A2
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                Sur le parking, avant de partir · QR + QCM
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-orange-300 flex-shrink-0" />
          </Link>
        </div>

        <div className="mb-4 flex items-end justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-500 mb-1">
              Nos parcours premium
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-foreground tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Formations complètes en immersif
            </h2>
          </div>
          <span className="hidden sm:inline text-[10px] italic text-muted-foreground/70">
            2 parcours · 30+ chapitres · Projetables en salle
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {/* CARTE PERMIS AM */}
          <Link
            to="/permis-am"
            className="group relative overflow-hidden rounded-2xl border border-red-500/30 hover:border-red-500/70 hover:shadow-[0_0_40px_-10px_rgba(207,32,49,0.5)] bg-gradient-to-br from-red-600/15 via-red-500/8 to-transparent p-5 sm:p-6 transition-all"
            data-testid="parcours-am"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400">
                Permis AM · BSR
              </div>
              <span className="text-[9px] font-black uppercase tracking-widest text-red-500 bg-red-500/10 border border-red-500/40 rounded px-1.5 py-0.5">NEW</span>
            </div>
            <div className="text-5xl sm:text-6xl font-black leading-none text-red-500/25 mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>AM</div>
            <h3 className="text-lg sm:text-xl font-black leading-tight text-foreground mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Cyclomoteur · Dès 14 ans
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
              8 heures obligatoires · Journée 1 : Tu découvres · Journée 2 : Tu deviens conducteur. 28 infographies projetables en salle.
            </p>
            <div className="flex items-center justify-between pt-3 border-t border-red-500/20">
              <span className="text-[10px] italic text-muted-foreground/80">2 journées · 28 chapitres</span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-red-400 group-hover:gap-2.5 transition-all">
                Découvrir <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
          {/* CARTE FORMATION 125 */}
          <Link
            to="/formation-125"
            className="group relative overflow-hidden rounded-2xl border border-orange-500/30 hover:border-orange-500/70 hover:shadow-[0_0_40px_-10px_rgba(245,158,11,0.5)] bg-gradient-to-br from-orange-500/15 via-orange-500/8 to-transparent p-5 sm:p-6 transition-all"
            data-testid="parcours-125"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-500/15 border border-orange-500/30 text-[10px] font-black uppercase tracking-widest text-orange-400">
                Formation 125 · Passerelle B → A1
              </div>
              <span className="text-[9px] font-black uppercase tracking-widest text-orange-500 bg-orange-500/10 border border-orange-500/40 rounded px-1.5 py-0.5">NEW</span>
            </div>
            <div className="text-5xl sm:text-6xl font-black leading-none text-orange-500/25 mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>125</div>
            <h3 className="text-lg sm:text-xl font-black leading-tight text-foreground mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              De l'automobiliste au pilote 2R
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
              7 heures obligatoires · Comprendre 🔴 · Maîtriser 🟠 · Anticiper 🔵. Titulaires du permis B (2 ans mini).
            </p>
            <div className="flex items-center justify-between pt-3 border-t border-orange-500/20">
              <span className="text-[10px] italic text-muted-foreground/80">3 modules · 60 chapitres</span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-orange-400 group-hover:gap-2.5 transition-all">
                Découvrir <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* =====================================================================
          🆓 CTA · LIVRET AM — Lire en ligne OU Télécharger (mobile-first)
          ===================================================================== */}
      <section className="mb-6" data-testid="home-pdf-livret-am">
        <div className="group relative overflow-hidden rounded-2xl border-2 border-red-500/50 bg-gradient-to-br from-red-600 via-red-500 to-rose-600 p-5 sm:p-6 shadow-[0_10px_40px_-10px_rgba(207,32,49,0.6)] flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-shrink-0 flex items-center gap-3">
            <div className="w-16 h-20 sm:w-20 sm:h-24 rounded-lg bg-white/15 border-2 border-white/30 flex flex-col items-center justify-center shadow-inner">
              <span className="text-[9px] font-black tracking-widest text-white/80">PDF</span>
              <span className="text-2xl sm:text-3xl leading-none">📕</span>
              <span className="text-[9px] font-black text-white/80">2026</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/25 border border-white/40 text-[10px] font-black uppercase tracking-widest text-white mb-2">
              🎁 Gratuit · Sans compte
            </div>
            <h3 className="text-lg sm:text-2xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Le livret AM Topolino 8H<br className="hidden sm:block"/> — 68 pages, édition 2026
            </h3>
            <p className="text-[12.5px] sm:text-sm text-white/90 leading-relaxed mt-1.5">
              4 parties · 32 chapitres avec références Legifrance · 20 situations-pièges · fiches de révision · glossaire. <strong>Utilisable par tout enseignant ou auto-école.</strong>
            </p>
          </div>
          <div className="flex-shrink-0 flex flex-col sm:flex-col gap-2 w-full sm:w-auto">
            <Link
              to="/livret-am/lecteur"
              className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 h-11 sm:h-12 rounded-xl bg-white text-red-600 font-black text-[13px] uppercase tracking-widest shadow-lg hover:scale-[1.03] active:scale-[0.98] transition-transform whitespace-nowrap"
              data-testid="home-btn-read-livret-am-online"
            >
              <BookOpen className="w-5 h-5" /> Lire en ligne
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/livrets/am-8h-2026/pdf?dl=1`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 h-11 sm:h-12 rounded-xl bg-black/40 border border-white/40 text-white font-black text-[13px] uppercase tracking-widest shadow-lg hover:bg-black/50 hover:scale-[1.03] active:scale-[0.98] transition-all whitespace-nowrap"
              data-testid="home-btn-download-livret-am-pdf"
            >
              <Download className="w-5 h-5" /> Télécharger
            </a>
          </div>
        </div>
      </section>

      {/* ====================================================================
          🎯 9 DÉMOS PÉDAGOGIQUES — Section Home ultra visible
          Charte Code 2 Roues · Mobile-first · Fun & Pro
          ==================================================================== */}
      <FeaturedDemosSection variant="home" />

      {/* ====================================================================
          RÉVISIONS RAPIDES — cartes horizontales swipeables (style Netflix/Apple TV)
          ====================================================================
          Mobile : scroll horizontal natif avec scroll-snap, défilement tactile
          Desktop : même grille horizontale, snap + ombre subtile au hover
          Aucun texte superflu, juste image + titre + 1 méta-info. */}
      <section
        className="relative -mx-4 sm:mx-0"
        data-testid="home-revisions-rapides"
        aria-labelledby="revisions-title"
        data-reveal=""
      >
        {/* Header section — ultra discret, pas de bla-bla */}
        <div className="flex items-end justify-between mb-4 sm:mb-5 px-4 sm:px-0">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Explorer
              </span>
            </div>
            <h2
              id="revisions-title"
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Découvrir nos modules
            </h2>
          </div>
          <button
            type="button"
            onClick={() => navigate('/revision')}
            className="text-[11px] sm:text-xs font-bold text-foreground/70 hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center gap-1 hide-in-oral"
            data-testid="revisions-see-all"
          >
            Tout voir <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Rail horizontal scrollable */}
        <div
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 sm:pb-2 px-4 sm:px-0 hide-scrollbar"
          style={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          {[
            {
              label: 'Plateau',
              meta: '6 manœuvres · 10 min',
              to: '/epreuves/plateau',
              image:
                'https://images.unsplash.com/photo-1558981852-426c6c22a060?w=900&q=80&auto=format&fit=crop',
              accent: '#cf2031',
              tag: 'OFFICIEL',
            },
            {
              label: 'Apprendre',
              meta: '9 thèmes · Magazine pédagogique',
              to: '/revision',
              image:
                'https://images.unsplash.com/photo-1558981359-219d6364c9c8?w=900&q=80&auto=format&fit=crop',
              accent: '#1d4ed8',
              tag: 'NOUVEAU',
            },
            {
              label: 'Circulation',
              meta: '30 min route ouverte',
              to: '/epreuves/circulation',
              // Image moto sur route ouverte
              image:
                'https://images.unsplash.com/photo-1547549082-6bc09f2049ae?w=900&q=80&auto=format&fit=crop',
              accent: '#16a34a',
            },
            {
              label: 'Trajectoires',
              meta: 'La courbe parfaite',
              to: '/epreuves/trajectoires',
              image:
                'https://images.unsplash.com/photo-1622185135505-2d795003994a?w=900&q=80&auto=format&fit=crop',
              accent: '#7c3aed',
            },
            {
              label: 'Vérifications',
              meta: '12 points de contrôle',
              to: '/magazine/equipement-complet-motard-2026',
              image:
                'https://images.unsplash.com/photo-1544148024-c3759a58ba99?w=900&q=80&auto=format&fit=crop',
              accent: '#f59e0b',
            },
          ].map((card) => (
            <button
              key={card.label}
              type="button"
              onClick={() => navigate(card.to)}
              data-testid={`revision-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="group relative flex-shrink-0 overflow-hidden rounded-xl bg-card transition-all duration-300 hover:scale-[1.02] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
              style={{
                width: 'clamp(220px, 72vw, 320px)',
                aspectRatio: '4 / 5',
                scrollSnapAlign: 'start',
                boxShadow: '0 8px 24px -16px rgba(0,0,0,0.5)',
              }}
            >
              {card.image ? (
                <>
                  {/* Image full bleed */}
                  <img
                    src={card.image}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    style={{ filter: 'saturate(0.95) contrast(1.05)' }}
                  />
                  {/* Dégradé éditorial */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(15,12,8,0) 0%, rgba(15,12,8,0.20) 45%, rgba(15,12,8,0.92) 100%)',
                    }}
                  />
                </>
              ) : (
                <>
                  {/* Fallback éditorial : gradient color + grand pictogramme */}
                  <div
                    className="absolute inset-0 transition-transform duration-700 group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, ${card.gradientFrom || '#222'} 0%, ${card.gradientTo || card.accent} 100%)`,
                    }}
                  />
                  {/* Pattern de fond subtil (lignes obliques) */}
                  <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.5) 8px 9px)',
                    }}
                  />
                  {/* Grand pictogramme central */}
                  {card.icon && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <card.icon
                        className="w-32 h-32 text-white/15 transition-transform duration-700 group-hover:scale-110"
                        strokeWidth={1.2}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                  {/* Dégradé bas pour la zone titre */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(15,12,8,0) 50%, rgba(15,12,8,0.85) 100%)',
                    }}
                  />
                </>
              )}
              {/* Tag (top left) — uniquement si défini, ultra discret */}
              {card.tag && (
                <span
                  className="absolute top-3 left-3 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.14em] text-white backdrop-blur-md"
                  style={{ background: `${card.accent}E6` }}
                >
                  {card.tag}
                </span>
              )}
              {/* Filet d'accent en haut */}
              <span
                className="absolute top-0 inset-x-0 h-[3px]"
                style={{ background: card.accent }}
              />
              {/* Bloc texte en bas */}
              <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 text-left">
                <h3
                  className="text-xl sm:text-2xl font-extrabold tracking-tight text-white leading-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {card.label}
                </h3>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[11px] sm:text-xs text-white/75 font-medium truncate">
                    {card.meta}
                  </span>
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 group-hover:bg-[hsl(var(--primary))] group-hover:border-[hsl(var(--primary))] transition-colors flex-shrink-0 ml-2"
                    aria-hidden="true"
                  >
                    <ArrowRight className="w-3.5 h-3.5 text-white" />
                  </span>
                </div>
              </div>
            </button>
          ))}

          {/* Padding fantôme pour permettre au dernier item de "snap" sur mobile */}
          <div className="flex-shrink-0 w-2 sm:hidden" aria-hidden="true" />
        </div>
      </section>

      {/* ====================================================================
          GUIDES PREMIUM — Layout asymétrique magazine
          ====================================================================
          Extrait dans /components/home/GuidesPremium.jsx (juin 2026) pour
          réduire la taille de Home.jsx. Voir le composant pour la grille
          complète (1 hero + 1 wide + 4 squares). */}
      <GuidesPremium />

      {/* ====================================================================
          PLATEAU INTERACTIF — Les 6 manœuvres officielles en swipe
          ====================================================================
          Extrait dans /components/home/PlateauInteractive.jsx (juin 2026)
          pour réduire la taille de Home.jsx. */}
      <PlateauInteractive />

      {/* ====================================================================
          MA PROGRESSION — Gamification discrète & premium
          ====================================================================
          Style Duolingo / Nike Run Club / Netflix progression :
          • Anneau XP circulaire avec gradient rouge → orange
          • Mini-stats : streak, examens, trophées
          • Badges récents (rail horizontal swipeable)
          • Prochaine étape suggérée (intelligent CTA)

          Variants :
          • Connecté : données réelles via api.getGamification()
          • Non connecté : teaser "Crée ton compte" avec valeurs masquées */}
      {/* ===== SECTION PROGRESSION — RETIRÉE (juin 2026) =====
          La section "Avance à ton rythme" (XP/streak/badges) appartenait à
          l'ancien format QCM. Elle est désactivée pour rester cohérente avec
          le pivot magazine ("sans score, sans chrono"). Le composant
          HomeProgressionSection est extrait dans components/home/ pour rollback. */}
      {false && (
        <HomeProgressionSection
          user={user}
          gamification={gamification}
          progress={progress}
          navigate={navigate}
        />
      )}

      {/* ===== SECTION LIVRET D'APPRENTISSAGE — Pédagogique · ludique · auto-évaluant =====
          Présentation du référentiel REMC A et accès à l'auto-évaluation des 38 sous-compétences.
          Mode connecté : progression réelle + badges débloqués.
          Mode visiteur : teaser des 4 compétences + CTA inscription. */}
      <Reveal>
        <HomeLivretSection />
      </Reveal>

      {/* ===== SECTION MAGAZINE — 9 dossiers pédagogiques =====
          Format 100 % éditorial (magazine) — dossiers thématiques rédigés
          par des moniteurs ECSR. Aucune banque de questions ETG. */}
      <Reveal>
        <HomeMagazineEtmSection />
      </Reveal>

      {/* ===== PROMO LANCEMENT SHORTS COMMUNAUTÉ (Oct 2026) =====
          Annonce la nouvelle rubrique de contribution communautaire. Dismissible
          par session. Message aligné avec le lancement cross-site CodeVoiture.fr. */}
      <Reveal>
        <ShortsPromoBanner variant="home" dismissible />
      </Reveal>

      {/* ===== CARROUSEL SHORTS DU MOMENT (Phase 36) =====
          10 vidéos verticales : 2 dossiers Code 2 Roues + 8 campagnes officielles
          « Parlons Moto avec Ornella Ongaro » (Sécurité Routière). Scroll
          horizontal natif + chevrons desktop, chaque carte ouvre /shorts?s=<id>. */}
      <Reveal>
        <HomeShortsCarousel />
      </Reveal>

      {/* ===== SECTION ENQUÊTES & TÉMOIGNAGES — FUSIONNÉE (août 2026) =====
          Retirée de la Home pour éviter le doublon avec HomeMagazineEtmSection.
          Reste accessible via la page /enquetes et le lien depuis Magazine. */}
      {false && (
        <Reveal>
          <HomeEnquetesSection />
        </Reveal>
      )}

      {/* CARTE PROMO — Ressources vidéo officielles (visible surtout mobile) */}
      <Reveal>
        <button
          type="button"
          onClick={() => navigate('/ressources-officielles')}
          className="w-full text-left group mc-card mc-card-accent-official relative overflow-hidden p-4 sm:p-5 cursor-pointer hover:border-[hsl(var(--primary))] transition-colors"
          data-testid="home-videos-promo"
        >
          <div
            className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-25"
            style={{ background: 'hsl(var(--primary))' }}
          />
          <div className="relative flex items-center gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl grid place-items-center bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex-shrink-0">
              <Film className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider leading-none">
                  Nouveau
                </span>
                <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                  100 % officiel
                </span>
              </div>
              <h3 className="text-sm sm:text-base font-black tracking-tight leading-tight">
                Vidéos & simulateur officiels Sécurité routière
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 sm:line-clamp-1">
                Simulateur #EquipezVous + 4 campagnes pour renforcer ton apprentissage.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[hsl(var(--primary))] group-hover:translate-x-1 transition-all flex-shrink-0" />
          </div>
        </button>
      </Reveal>

      {/* CHIFFRES OFFICIELS SÉCURITÉ ROUTIÈRE — Source ONISR live (cache annuel) */}
      <Reveal>
        <SecuriteRoutiereStats variant="compact" />
      </Reveal>

      {/* BANNIÈRES VIVANTES — Météo · MotoGP (Sécurité routière supprimée : doublon avec SecuriteRoutiereStats ci-dessus) */}
      <section className="grid md:grid-cols-2 gap-4" data-snap-on-mobile="" data-testid="home-live-banners" data-reveal="" data-reveal-delay="100">

        {/* Météo motard */}
        <div className="mc-card p-5 relative overflow-hidden" style={{ borderTop: `4px solid ${advice?.color || '#1d4ed8'}` }} data-testid="banner-meteo">
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-20" style={{ background: advice?.color || '#1d4ed8' }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${advice?.color || '#1d4ed8'}15`, color: advice?.color || '#1d4ed8' }}>
                <WeatherIcon className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: advice?.color || '#1d4ed8' }}>Météo motard</div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5" />
                  {weatherLoading ? 'Localisation…' : (weather?.city || 'Paris')}
                </div>
              </div>
            </div>
            {weatherLoading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Chargement…</div>
            ) : weather?.temp_c !== null && weather?.temp_c !== undefined ? (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-black tabular-nums" style={{ color: advice?.color }}>{weather.temp_c}°</span>
                  <span className="text-xs font-bold text-muted-foreground capitalize">{weather.condition}</span>
                </div>
                <div className="text-xs text-foreground/80 leading-relaxed">
                  Ressenti <strong>{weather.feels_c}°</strong> · Vent <strong>{weather.wind_kmh} km/h</strong> · Humidité <strong>{weather.humidity} %</strong>
                </div>
                {advice && (
                  <div className="mt-3 p-2 rounded-lg text-xs italic" style={{ background: `${advice.color}12`, color: advice.color }}>
                    <strong className="uppercase tracking-widest text-[9px] block mb-0.5">{advice.label}</strong>
                    {advice.text}
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Météo indisponible</div>
            )}
          </div>
        </div>

        {/* MotoGP */}
        <div className="mc-card p-5 relative overflow-hidden" style={{ borderTop: '4px solid #18181b' }} data-testid="banner-motogp">
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-15" style={{ background: '#18181b' }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#18181b] text-white">
                <Trophy className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#18181b]">MotoGP 2026</div>
                <div className="text-[10px] text-muted-foreground">Prochain Grand Prix · Round {nextGP.round}/20</div>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-black tabular-nums">
                J-{gpDays}
              </span>
              <span className="text-xs font-bold text-muted-foreground">
                {new Date(nextGP.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50">
              <span className="text-2xl">{nextGP.flag}</span>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm truncate">{nextGP.country}</div>
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                  <Flag className="w-3 h-3" /> {nextGP.city}
                </div>
              </div>
            </div>
            <a
              href="https://www.motogp.com/fr/calendrier"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[hsl(var(--primary))] hover:underline"
              data-testid="banner-motogp-link"
            >
              Calendrier complet <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* CONSEIL ECSR — Article du moment (Magazine) */}
      <Reveal>
        <button
          type="button"
          onClick={() => navigate('/magazine/bien-choisir-casque-moto')}
          className="w-full text-left group relative overflow-hidden mc-card cursor-pointer hover:border-[hsl(var(--primary))] transition-colors"
          data-testid="home-conseil-ecsr"
          aria-label="Lire l'article : Bien choisir son casque de moto"
        >
          <div className="grid md:grid-cols-[280px_1fr] gap-0">
            {/* Image */}
            <div className="relative h-44 md:h-full min-h-[180px] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=1200&q=75&auto=format&fit=crop"
                alt="Casque de moto intégral noir"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                style={{ filter: 'saturate(0.92) contrast(1.05)' }}
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(180deg, rgba(15,12,8,0.10) 0%, rgba(15,12,8,0.55) 100%)' }}
              />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--primary))] text-white text-[9px] font-black uppercase tracking-wider">
                Conseil ECSR
              </div>
            </div>
            {/* Contenu */}
            <div className="p-4 sm:p-6 flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                  Article du moment · Sécurité
                </span>
                <span className="text-[10px] text-foreground/55 font-medium italic whitespace-nowrap">
                  ~ 4 min de lecture
                </span>
              </div>
              <h3
                className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight leading-tight mb-2"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Bien choisir son casque de moto
              </h3>
              <p className="text-sm text-foreground/75 leading-relaxed mb-3 max-w-2xl">
                Norme <strong>ECE 22-06</strong>, casque jet vs intégral, sanctions, conseils
                d&apos;essai et d&apos;entretien : le guide ECSR pour ne pas se tromper.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[hsl(var(--primary))] group-hover:translate-x-1 transition-transform">
                  Lire l&apos;article <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </div>
        </button>
      </Reveal>

      {/* CARROUSEL FORMATIONS avec photos cinematic */}
      <section data-testid="home-formations-visuel" data-reveal="">
        <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">Parcours de formation</div>
            <h2 className="text-xl sm:text-3xl font-black mt-1 tracking-tight">Trouve ton chemin vers les 2 roues.</h2>
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => navigate('/formation')} data-testid="formations-voir-tout">
            Voir tout <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { key: 'AM / BSR', label: 'AM · Voiturette', age: 'Dès 14 ans' },
            { key: 'A1', label: 'A1', age: 'Dès 16 ans' },
            { key: 'A2', label: 'A2', age: 'Dès 18 ans' },
            { key: 'Passerelle A2 vers A', label: 'Passerelle', age: 'A2 → A' },
            { key: 'Formation 125 (B→A1)', label: '125 (B)', age: '7h pratique' },
            { key: 'L5e / Ryker 600-900', label: 'L5e · Ryker', age: '3 roues' },
          ].map((f, i) => (
            <button
              key={i}
              onClick={() => navigate(`/formation?formation=${encodeURIComponent(f.key)}`)}
              data-testid={`formation-card-${f.label.replace(/\s+/g,'-').toLowerCase()}`}
              className="c2r-img-card c2r-touch-feedback group relative overflow-hidden rounded-2xl aspect-[3/4] hover:-translate-y-1 hover:shadow-xl transition-all"
            >
              <img
                src={FORMATION_IMAGES[f.key]}
                alt={f.label}
                className="c2r-img-card-img absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,17,22,0.1) 0%, rgba(15,17,22,0.85) 100%)' }} />
              <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                <div className="text-[9px] font-black uppercase tracking-widest text-[#ff7a84]">{f.age}</div>
                <div className="font-black text-base text-foreground leading-tight mt-0.5">{f.label}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* 2 espaces — version condensée */}
      {/* Section "Espaces" : 2 cartes complémentaires pour les 2 cibles
          principales : 1) candidats au permis moto, 2) jeunes scolaires
          (ASSR1, ASSR2). L'ancienne carte "Futur enseignant CCS" a été
          déplacée en lien discret en bas de page (cf. footer + le bloc
          "Espace pro" plus bas) pour ne pas concurrencer le message
          pédagogique élève. */}
      <section className="grid md:grid-cols-2 gap-5" data-testid="home-espaces" data-reveal="">
        {/* Carte 1 — FUTUR MOTARD (cible principale) */}
        <div className="mc-card mc-card-accent-training mc-card-interactive p-7 relative overflow-hidden group hover:shadow-xl transition-shadow">
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity" style={{ background: '#cf2031' }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary-soft))] px-3 py-1 text-xs font-bold text-[hsl(var(--primary-dark))] mb-3">
              <GraduationCap className="w-3.5 h-3.5" /> FUTUR MOTARD
            </div>
            <h3 className="text-xl sm:text-2xl font-black tracking-tight mb-1">Réussis ton permis 2 roues.</h3>
            <p className="text-sm text-foreground/80 mb-5">AM, A1, A2, Passerelle A, 125 (B), L5e. Parcours pédagogique en format magazine, sans pression.</p>
            <ul className="space-y-2 mb-5">
              {['Parcours guidé par des moniteurs ECSR', '10 scénarios de conduite réelle', `${stats.themes} dossiers magazine · 31 enquêtes`].map((it, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))] flex-shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <Button className="mc-btn-primary" onClick={() => navigate('/parcours')} data-testid="espace-eleve-cta">
              Commencer mon parcours <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>

        {/* Carte 2 — JEUNES & SCOLARITÉ (cible secondaire pédagogique) */}
        <div className="mc-card mc-card-interactive p-7 relative overflow-hidden group hover:shadow-xl transition-shadow" style={{ borderTop: '4px solid #0ea5e9' }}>
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity" style={{ background: '#0ea5e9' }} />
          <div className="relative">
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold mb-3"
              style={{ background: '#0ea5e91a', color: '#0ea5e9' }}
            >
              <GraduationCap className="w-3.5 h-3.5" /> JEUNES & SCOLARITÉ
            </div>
            <h3 className="text-xl sm:text-2xl font-black tracking-tight mb-1">ASSR, BSR, AM : tu commences ?</h3>
            <p className="text-sm text-foreground/80 mb-5">
              Tu es au collège ou tu prépares ton premier deux-roues ? On t’explique chaque attestation et son rôle dans ton parcours.
            </p>
            <ul className="space-y-2 mb-5">
              {[
                'ASSR 1 (5ᵉ) → ouvre le permis AM (scooter 50 dès 14 ans)',
                'ASSR 2 (3ᵉ) → obligatoire pour A1, A2, B (permis voiture)',
                "ASR pour adultes · AER pour établissements spécialisés",
              ].map((it, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#0ea5e9' }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="rounded-full font-bold"
              style={{ borderColor: '#0ea5e9', color: '#0ea5e9' }}
              onClick={() => navigate('/assr')}
              data-testid="espace-assr-cta"
            >
              Découvrir le hub ASSR <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </section>

      {/* RUBAN ÉDITORIAL D'IDENTITÉ — version native (sans image bannière).
          Remplace la bannière JPG (qui était croppée par object-fit: cover) par
          un panneau éditorial 100% Tailwind : tagline + 3 pastilles de
          confiance. Aucune image = aucun risque de cropping, chargement
          instantané, accessibilité parfaite, parfaitement responsive. */}
      <section
        className="relative overflow-hidden rounded-sm border border-border/60 bg-card cursor-pointer group"
        onClick={() => navigate('/parcours')}
        role="link"
        aria-label="Démarrer le parcours CODE 2 ROUES"
        data-testid="home-brand-stripe"
        data-reveal=""
      >
        {/* Filet rouge fin en haut (signature magazine) */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-[hsl(var(--primary))]" />
        {/* Halo discret côté droit, dans la charte */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-[0.08] blur-3xl"
          style={{ background: 'radial-gradient(closest-side, hsl(var(--primary)) 0%, transparent 70%)' }}
        />

        <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-10 items-center px-5 sm:px-7 lg:px-9 py-7 sm:py-8 lg:py-9">
          {/* Bloc gauche — tagline éditoriale */}
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.32em] text-[hsl(var(--primary))] mb-3">
              <span className="h-px w-6 bg-[hsl(var(--primary))]" />
              CODE 2 ROUES
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-[34px] font-black leading-[1.1] tracking-tight"
              data-testid="brand-stripe-title"
            >
              Le 2 roues, notre passion.{' '}
              <span className="text-[hsl(var(--primary))]">Ta liberté.</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-foreground/70 max-w-xl leading-relaxed">
              Apprends · Progresse · Reste en sécurité. La plateforme pédagogique
              pensée par des moniteurs moto agréés ECSR.
            </p>
          </div>

          {/* Bloc droit — Stats 9/31/100% RETIRÉES (août 2026) : doublon avec la section "Le code moto façon magazine" */}
        </div>

        {/* Filet rouge fin en bas (cohérent avec le hero éditorial) */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[hsl(var(--primary))]/70" />
      </section>

      {/* Features grid — RETIRÉE (août 2026) pour réduire la longueur de la Home */}
      {/* Chiffres clés animés — RETIRÉ (août 2026) : stats marketing redondantes avec le contenu magazine plus haut */}
      {false && (
      <>
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="home-stats" data-reveal="">
        {[
          { value: 9, suffix: '', label: 'Dossiers magazine', testid: 'stat-dossiers' },
          { value: 31, suffix: '', label: 'Enquêtes magazine', testid: 'stat-magazine' },
          { value: 72, suffix: '', label: 'Articles satellites', testid: 'stat-satellites' },
          { value: 100, suffix: '%', label: 'Gratuit, pour tous', testid: 'stat-free' },
        ].map((s, i) => (
          <div
            key={s.testid}
            className="c2r-spotlight mc-card p-5 text-center"
            data-reveal=""
            data-reveal-delay={String(100 + i * 100)}
          >
            <div className="relative z-[2]">
              <div className="text-3xl sm:text-4xl font-black tracking-tight" style={{ color: 'hsl(var(--primary))' }}>
                <Counter value={s.value} suffix={s.suffix} duration={1400} testid={s.testid} />
              </div>
              <div className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-muted-foreground mt-1.5 leading-tight">
                {s.label}
              </div>
            </div>
          </div>
        ))}
      </section>

      <section data-testid="home-features" data-reveal="">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary-soft))] px-3 py-1 text-xs font-bold text-[hsl(var(--primary-dark))] mb-3">
            POURQUOI CODE 2 ROUES
          </div>
          <h2 className="text-[10px] font-black uppercase tracking-[0.28em] text-foreground/80 sm:text-xs">
            3 raisons de s’y mettre
          </h2>
        </div>
        {/* Carousel snap sur mobile, grille 3 colonnes sur desktop */}
        <div
          className="flex sm:grid sm:grid-cols-3 gap-4 sm:gap-5 overflow-x-auto sm:overflow-visible snap-x snap-mandatory pb-3 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth"
          style={{ scrollbarWidth: 'none' }}
        >
          {[
            { icon: Sparkles, t: 'Correction IA', d: 'Explication pédagogique à chaque question.' },
            { icon: Target, t: 'Par thème', d: 'Les 9 thèmes officiels expliqués en magazine, à ton rythme.' },
            { icon: Brain, t: 'Approche terrain', d: "Pensé par des moniteurs moto agréés ECSR." },
          ].map((h, i) => (
            <div
              key={i}
              className="c2r-spotlight mc-card p-5 sm:p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200 snap-start flex-shrink-0 w-[78%] sm:w-auto"
            >
              <div className="relative z-[2]">
                <div className="w-11 h-11 rounded-2xl mb-4 flex items-center justify-center" style={{ background: 'hsl(var(--primary-soft))' }}>
                  <h.icon className="w-5 h-5 text-[hsl(var(--primary))]" />
                </div>
                <h4 className="font-black text-base sm:text-lg mb-1.5 tracking-tight">{h.t}</h4>
                <p className="text-sm text-foreground/75 leading-relaxed">{h.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      </>
      )}

      {/* Bandeau Mobilités urbaines — EDPM & VAE */}
      <section
        className="mc-card p-6 sm:p-8 relative overflow-hidden c2r-noise"
       
        data-reveal=""
        data-reveal-variant="scale"
        data-testid="home-mobilites-banner"
      >
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-30" style={{ background: '#f59e0b' }} />
        <div className="absolute -bottom-12 -left-16 w-40 h-40 rounded-full blur-3xl opacity-20" style={{ background: '#18794e' }} />
        <div className="relative flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[10px] font-black text-foreground uppercase tracking-widest mb-3">
              <Zap className="w-3 h-3" />
              <span>Mobilités urbaines</span>
              <Bike className="w-3 h-3" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground leading-tight">
              Tu roules en trottinette ou en vélo électrique ?
            </h3>
            <p className="text-sm text-foreground/80 mt-2 max-w-xl">
              Trottinettes électriques (EDPM) et vélos à assistance électrique (VAE) partagent la route avec les motards. Règles, équipement et assurances — on t’explique tout.
            </p>
          </div>
          <div className="flex-shrink-0">
            <Button
              onClick={() => navigate('/mobilites')}
              className="c2r-sheen c2r-touch-feedback h-12 px-6 text-base rounded-full bg-white text-[#cf2031] hover:bg-white/90 font-black"
              data-testid="home-mobilites-cta"
            >
              Découvrir les mobilités <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Passerelle réciproque code2roues.fr ↔ codevoiture.fr (juin 2026).
          Callout institutionnel « plateforme sœur » — miroir du callout déjà
          présent sur codevoiture.fr. Emplacement thématique : après les
          mobilités (EDPM/VAE) et avant la newsletter, quand le lecteur a
          fini de parcourir tout le contenu moto. */}
      <CrossSiteCallout />

      {/* Newsletter — opt-in pour les nouveautés (toujours visible, même connecté) */}
      <section data-testid="home-newsletter" data-reveal="">
        <NewsletterForm
          variant="card"
          source="home"
          title="Reçois les nouveautés Code 2 Roues"
          subtitle="Une édition hebdo, les nouveaux modules, les corrections officielles. 0 pub, 0 spam — uniquement les avancées de la plateforme."
        />
      </section>

      {/* MAILLAGE INTERNE — RETIRÉ (août 2026) : les 5 liens "À lire ensuite" faisaient doublon
          avec le contenu déjà présenté plus haut (Magazine, Plateau, Formations, Parcours).
          Le SEO reste renforcé via le maillage naturel des Guides Premium et modules. */}
      {false && (
      <div data-reveal="">
        <RelatedLinks topic="home" max={5} />
      </div>
      )}

      {/* SÉLECTEUR 3 AUDIENCES — compact (août 2026) :
          fusionné en un seul bandeau horizontal avec 3 boutons rapides. */}
      <section
        id="audience"
        className="border-t border-border pt-6 pb-2 scroll-mt-24"
        data-testid="home-audience-selector"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] text-foreground/60">
              Trois parcours · Un même esprit moto
            </p>
            <h3
              className="text-lg sm:text-xl font-black tracking-tight mt-0.5"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Choisis ton point d'entrée
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Élève */}
          <Link
            to="/parcours"
            onClick={() => setAudience(AUDIENCES.ELEVE)}
            className={`group rounded-xl border bg-card p-3 sm:p-3.5 hover:border-[hsl(var(--primary))] transition-colors flex items-center gap-3 ${audience === AUDIENCES.ELEVE ? 'border-[hsl(var(--primary))] ring-1 ring-[hsl(var(--primary))]/30' : 'border-border'}`}
            data-testid="audience-eleve"
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
              style={{ background: '#cf2031' }}
            >
              <GraduationCap className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50 leading-none">Élève</div>
              <h4 className="text-[13px] font-black mt-0.5 leading-tight">Je prépare mon permis 2 roues</h4>
            </div>
            <ArrowRight className="w-4 h-4 text-foreground/40 group-hover:translate-x-0.5 group-hover:text-[hsl(var(--primary))] transition-all flex-shrink-0" />
          </Link>

          {/* Enseignant certifié */}
          <Link
            to={user ? "/enseignant" : "/login"}
            state={user ? undefined : { from: "/enseignant" }}
            onClick={() => { if (user) setAudience(AUDIENCES.ENSEIGNANT); }}
            className={`group rounded-xl border bg-card p-3 sm:p-3.5 hover:border-cyan-500 transition-colors flex items-center gap-3 relative ${audience === AUDIENCES.ENSEIGNANT ? 'border-cyan-500 ring-1 ring-cyan-500/30' : 'border-border'}`}
            data-testid="audience-enseignant"
          >
            {!user && (
              <span className="absolute -top-1.5 right-2 z-10 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white shadow bg-cyan-700">
                <Lock className="w-2 h-2" /> Compte
              </span>
            )}
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
              style={{ background: '#0891b2' }}
            >
              <Award className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50 leading-none">Enseignant certifié</div>
              <h4 className="text-[13px] font-black mt-0.5 leading-tight">J&apos;anime mes séances terrain</h4>
            </div>
            <ArrowRight className="w-4 h-4 text-foreground/40 group-hover:translate-x-0.5 group-hover:text-cyan-600 transition-all flex-shrink-0" />
          </Link>

          {/* Futur enseignant CCS */}
          <Link
            to={user ? "/preparation-ccs" : "/login"}
            state={user ? undefined : { from: "/preparation-ccs" }}
            onClick={() => { if (user) setAudience(AUDIENCES.FUTUR_ENSEIGNANT); }}
            className={`group rounded-xl border bg-card p-3 sm:p-3.5 hover:border-orange-500 transition-colors flex items-center gap-3 relative ${audience === AUDIENCES.FUTUR_ENSEIGNANT ? 'border-orange-500 ring-1 ring-orange-500/30' : 'border-orange-300/50'}`}
            data-testid="audience-futur-enseignant"
          >
            {!user ? (
              <span className="absolute -top-1.5 right-2 z-10 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white shadow bg-orange-700">
                <Lock className="w-2 h-2" /> Compte
              </span>
            ) : (
              <span className="absolute -top-1.5 right-2 z-10 rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white shadow bg-orange-600">
                Nouveau
              </span>
            )}
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
              style={{ background: '#ea580c' }}
            >
              <Award className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-black uppercase tracking-widest text-foreground/50 leading-none">Futur enseignant</div>
              <h4 className="text-[13px] font-black mt-0.5 leading-tight">Je prépare le CCS 2 Roues</h4>
            </div>
            <ArrowRight className="w-4 h-4 text-foreground/40 group-hover:translate-x-0.5 group-hover:text-orange-600 transition-all flex-shrink-0" />
          </Link>
        </div>
      </section>

      {/* Final CTA — RETIRÉ (août 2026) : le CTA géant "Le code moto façon magazine"
          était en doublon avec les CTA magazine déjà présents dans la Home. */}
      {false && <FinalCTA user={user} />}
    </div>
  );
}
